import { localeMeta } from "@/lib/seo-head";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Minus } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Frame, Section, SectionHeading, ZigzagRow } from "@/components/site/Section";
import { Reveal } from "@/components/site/Reveal";
import { GhostCta, PrimaryCta } from "@/components/site/Cta";
import { Lottie } from "@/components/site/Lottie";
import { IsoGrid } from "@/components/site/Iso";
import { HomeProductCollage } from "@/components/site/HomeProductCollage";
import { ISO } from "@/content/illustrations";
import { LOTTIE } from "@/content/animations";
import {
  ABOUT_POINTS as ABOUT_POINTS_SOURCE,
  CAPABILITIES as CAPABILITIES_SOURCE,
  CHAOS as CHAOS_SOURCE,
  ECOSYSTEM as ECOSYSTEM_SOURCE,
  FAQS as FAQS_SOURCE,
  HERO as HERO_SOURCE,
  INDUSTRIES as INDUSTRIES_SOURCE,
  RESULTS as RESULTS_SOURCE,
  SECURITY as SECURITY_SOURCE,
  SITE,
  TESTIMONIALS as TESTIMONIALS_SOURCE,
} from "@/content/site";
import { alternateLinks } from "@/lib/i18n";
import { useT, useTx } from "@/lib/i18n-dict";
import beedigitImg from "@/assets/product/beedigit.webp";
import modSalesImg from "@/assets/modules/sales.svg";
import modProductionImg from "@/assets/modules/production.svg";
import modSupplyImg from "@/assets/modules/purchases.svg";
import modInventoryImg from "@/assets/modules/stocks.svg";
import modPlanningImg from "@/assets/modules/planning.svg";
import modSchedulingImg from "@/assets/modules/scheduling.svg";
import modCostingImg from "@/assets/modules/cost.svg";
import modDataImg from "@/assets/modules/technical-data.svg";

const TITLE = "factory.online — sales, production & inventory for manufacturers";
const DESCRIPTION =
  "Cloud-based, plug-and-play system for sales, production and inventory in real time — built for manufacturers.";

export const Route = createFileRoute("/{-$locale}/")({
  staticData: { sitemap: true },
  head: ({ params }) => {
    const __m = localeMeta(params, TITLE, DESCRIPTION);
    return ({
    meta: [
      { title: __m.title },
      { name: "description", content: __m.description },
      { property: "og:title", content: __m.title },
      { property: "og:description", content: __m.description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: alternateLinks("/"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS_SOURCE.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: { "@type": "Answer", text: faq.a },
          })),
        }),
      },
    ],
  });
  },
  component: Home,
});

const CAPABILITY_VISUALS: Record<string, string> = {
  sales: modSalesImg,
  production: modProductionImg,
  purchases: modSupplyImg,
  stocks: modInventoryImg,
  planning: modPlanningImg,
  scheduling: modSchedulingImg,
  cost: modCostingImg,
  "technical-data": modDataImg,
};

function Home() {
  const t = useT();
  const HERO = useTx(HERO_SOURCE);
  const CHAOS = useTx(CHAOS_SOURCE);
  const CAPABILITIES = useTx(CAPABILITIES_SOURCE);
  const RESULTS = useTx(RESULTS_SOURCE);
  const TESTIMONIALS = useTx(TESTIMONIALS_SOURCE);
  const INDUSTRIES = useTx(INDUSTRIES_SOURCE);
  const SECURITY = useTx(SECURITY_SOURCE);
  const ABOUT_POINTS = useTx(ABOUT_POINTS_SOURCE);
  const ECOSYSTEM = useTx(ECOSYSTEM_SOURCE);
  const FAQS = useTx(FAQS_SOURCE);
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "var(--gradient-edge)" }}
        />
        <IsoGrid intensity="strong" />
        <div className="container-page relative grid items-center gap-10 py-16 md:grid-cols-[1.1fr_1fr] md:py-24 lg:gap-12">
          <Reveal>
            <h1 className="font-display text-4xl leading-[1.05] font-bold text-balance-tight sm:text-5xl md:text-[2.75rem] lg:text-6xl">
              {HERO.title}
            </h1>
            <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">{HERO.lead}</p>
            <ul className="mt-6 grid max-w-xl gap-2.5">
              {HERO.points.map((point) => (
                <li key={point} className="flex gap-3 text-sm text-muted-foreground">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                  {point}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-3">
              <PrimaryCta to={HERO.primaryCta.to}>{HERO.primaryCta.label}</PrimaryCta>
              <GhostCta to={HERO.secondaryCta.to}>{HERO.secondaryCta.label}</GhostCta>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="relative">
              <div
                aria-hidden
                className="pointer-events-none absolute inset-8 -z-10 rounded-full bg-primary/10 blur-3xl"
              />
              <Lottie
                src={LOTTIE.factory}
                className="mx-auto aspect-[16/9] w-full max-w-[400px]"
                ariaLabel="Isometric smart factory animation"
              />
              <HomeProductCollage />
            </div>
          </Reveal>
        </div>
      </section>

      {/* With / without */}
      <Section>
        <SectionHeading eyebrow="Why factory.online" title={CHAOS.title} lead={CHAOS.lead} />
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <Reveal className="rounded-xl border border-border bg-surface/50 p-7">
            <h3 className="font-display text-xl font-semibold text-muted-foreground">
              {CHAOS.without.title}
            </h3>
            <ul className="mt-6 space-y-4">
              {CHAOS.without.items.map((item) => (
                <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                  <Minus className="mt-0.5 size-4 shrink-0" aria-hidden />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal
            delay={80}
            className="rounded-xl border border-primary/30 bg-surface p-7 shadow-[var(--glow-primary)]"
          >
            <h3 className="font-display text-xl font-semibold">{CHAOS.with.title}</h3>
            <ul className="mt-6 space-y-4">
              {CHAOS.with.items.map((item) => (
                <li key={item} className="flex gap-3 text-sm">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </Section>

      {/* Capabilities — zigzag */}
      <Section>
        <SectionHeading
          eyebrow="The platform"
          title="Everything you need for modern manufacturing"
          lead="Eight modules on one data backbone. Turn on what you need, add the rest as you grow."
        />
        <div className="mt-6 divide-y divide-border/60">
          {CAPABILITIES.map((capability, index) => (
            <ZigzagRow
              key={capability.slug}
              index={index}
              eyebrow={`0${index + 1}`}
              title={capability.title}
              body={capability.summary}
              items={capability.items}
              action={
                <GhostCta to={`/${capability.slug}`} className="px-5 py-2.5">
                  {t("Explore")} {capability.title}
                </GhostCta>
              }
              visual={
                <Frame>
                  <img
                    src={CAPABILITY_VISUALS[capability.slug] ?? modSalesImg}
                    alt={`${capability.title} in factory.online`}
                    className="w-full rounded-lg"
                    width={800}
                    height={600}
                    loading="lazy"
                    decoding="async"
                  />
                </Frame>
              }
            />
          ))}
        </div>
      </Section>

      {/* Results */}
      <Section className="bg-surface/40">
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.8fr]">
          <SectionHeading
            eyebrow="Implementation results"
            title="What changes after go-live"
            lead="Measured across factory.online implementations."
          />
          <Lottie
            src={LOTTIE.dashboard}
            className="mx-auto aspect-square w-full max-w-[420px]"
            ariaLabel="Isometric analytics dashboard animation"
          />
        </div>
        <div className="mt-12 grid gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
          {RESULTS.map((result, index) => (
            <Reveal key={result.label} delay={index * 40} className="bg-background p-8">
              <p className="font-display text-3xl font-bold text-primary md:text-4xl">
                {t(result.value)}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{result.label}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Testimonials */}
      <Section>
        <SectionHeading eyebrow="Trusted by manufacturers" title="Results from the shop floor" />
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {TESTIMONIALS.map((testimonial, index) => (
            <Reveal
              key={testimonial.name}
              delay={index * 60}
              className="flex h-full flex-col rounded-xl border border-border bg-surface/50 p-7"
            >
              <p className="font-display text-lg font-semibold">{testimonial.highlight}</p>
              <p className="mt-4 flex-1 text-sm text-muted-foreground">{testimonial.quote}</p>
              <p className="mt-6 text-sm">
                <span className="font-medium">{testimonial.name}</span>
                <span className="text-muted-foreground"> — {testimonial.role}</span>
              </p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Industries */}
      <Section className="relative overflow-hidden bg-surface/40">
        <img
          src={ISO.teamwork}
          alt=""
          aria-hidden
          loading="lazy"
          className="pointer-events-none absolute -right-32 top-0 -z-10 hidden w-[560px] opacity-[0.07] lg:block"
        />
        <SectionHeading
          eyebrow="Industries"
          title="Built for discrete and process manufacturing"
        />
        <div className="mt-10 flex flex-wrap gap-3">
          {INDUSTRIES.map((industry, index) => (
            <Reveal key={industry} delay={index * 30}>
              <span className="inline-flex rounded-full border border-border bg-background px-5 py-2.5 text-sm text-muted-foreground">
                {industry}
              </span>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Security */}
      <Section>
        <SectionHeading eyebrow="Security" title="Bank-level security" />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {SECURITY.map((item, index) => (
            <Reveal
              key={item.title}
              delay={index * 50}
              className="rounded-xl border border-border p-6"
            >
              <h3 className="font-display text-base font-semibold">{item.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{item.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Ecosystem */}
      <Section className="bg-surface/40">
        <ZigzagRow
          index={0}
          eyebrow="Ecosystem"
          title={ECOSYSTEM.title}
          body={ECOSYSTEM.lead}
          items={ECOSYSTEM.items}
          visual={
            <Frame>
              <img
                src={beedigitImg}
                alt="BEEDIGIT equipment monitoring dashboard"
                className="w-full rounded-lg"
                width={760}
                height={1478}
                loading="lazy"
                decoding="async"
              />
            </Frame>
          }
          action={
            <a
              href={SITE.beedigitUrl}
              className="text-sm font-semibold text-primary hover:underline"
            >
              {t("Learn about BEEDIGIT →")}
            </a>
          }
        />
      </Section>

      {/* Who we are */}
      <Section>
        <div className="grid gap-10 lg:grid-cols-2">
          <SectionHeading
            eyebrow="Who we are"
            title="Manufacturing expertise that works across borders"
          />
          <Reveal delay={80}>
            <ul className="grid gap-4">
              {ABOUT_POINTS.map((point) => (
                <li key={point} className="flex gap-3 text-sm text-muted-foreground">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                  {point}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="bg-surface/40" id="faq">
        <SectionHeading eyebrow="FAQ" title="Questions manufacturers ask us" />
        <Reveal className="mt-10 max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((faq, index) => (
              <AccordionItem key={faq.q} value={`faq-${index}`}>
                <AccordionTrigger className="text-start font-display text-base">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </Section>

      {/* Closing CTA */}
      <Section>
        <Reveal className="rounded-2xl border border-primary/30 bg-surface p-10 text-center shadow-[var(--glow-primary)] md:p-16">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-balance-tight md:text-4xl">
            {t("Your factory deserves better than spreadsheets and guesswork")}
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryCta to="/registration-demo">Book a demo</PrimaryCta>
            <GhostCta to="/contacts">Talk to a consultant</GhostCta>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
